import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, TrophyIcon, PlayIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { DefaultService, Player, PlayerWithLeagueContext, PlayOutput, PlayStats } from '../generated-api';
import PlayList from '../components/common/PlayList';
import PlayFilterWidget from '../components/common/PlayFilterWidget';
import StatCardsCarousel from '../components/common/StatCardsCarousel';
import PageHeader from '../components/common/PageHeader';
import FilterButton from '../components/common/FilterButton';
import SearchButton from '../components/common/SearchButton';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useJobPoller } from '../hooks/useJobPoller';
import { trackAction } from '../services/telemetry';

const PlayListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { games, leagues, fetchGames, fetchLeagues } = useData();
  const { isAdmin } = useAuth();
  const [plays, setPlays] = useState<PlayOutput[]>([]);
  const [players, setPlayers] = useState<(Player | PlayerWithLeagueContext)[]>([]);
  const [playStats, setPlayStats] = useState<PlayStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const recalcPoller = useJobPoller('global', 'rating_recalculation', () => loadData());

  // Filter states
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedPlayerCount, setSelectedPlayerCount] = useState<string>('');
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    try {
      setError(null);
      // Use cached games and leagues, only fetch plays and players fresh
      const [playsData, playersData, statsData] = await Promise.all([
          DefaultService.getPlaysPlaysGet({}),
          DefaultService.getPlayersPlayersGet(),
          DefaultService.getPlayStats({}),
          fetchGames(),
          fetchLeagues()
      ]);
      setPlays(playsData);
      setPlayers(playersData);
      setPlayStats(statsData);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchPlays = useCallback(async () => {
    try {
      const playsData = await DefaultService.getPlaysPlaysGet({});
      setPlays(playsData);
    } catch (err) {
      console.error('Error refetching plays:', err);
    }
  }, []);

  useEffect(() => {
    // Check for gameId parameter to set initial filter
    const gameId = searchParams.get('gameId');
    if (gameId) {
      setSelectedGame(gameId);
    }
  }, [searchParams]);

  const handleDeletePlay = async (playId: string) => {
    if (!window.confirm('Are you sure you want to delete this play session? This action cannot be undone and will not recalculate ratings automatically.')) {
      return;
    }

    try {
      await DefaultService.deletePlayPlaysPlayIdDelete({ playId: playId });
      trackAction('play_deleted', { from: 'list' });
      setPlays(plays.filter(p => p.id !== playId));
    } catch (err) {
      setError('Failed to delete play session. Please try again.');
      console.error('Error deleting play:', err);
    }
  };

  const handleRecalculateRatings = async () => {
    if (!window.confirm('This will recalculate all player ratings from scratch based on play history. Continue?')) {
      return;
    }

    try {
      const result = await DefaultService.recalculateAllRatingsRatingsRecalculateAllPost();
      recalcPoller.startJob(result.jobId);
    } catch (err) {
      setError('Failed to recalculate ratings. Please try again.');
      console.error('Error recalculating ratings:', err);
    }
  };

  // Filter logic
  const getPlayerCountForPlay = (play: PlayOutput): number => {
    return Object.values(play.teams).flat().length;
  };

  const filteredPlays = plays.filter(play => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      // Search in game name
      const game = games.find(g => g.id === play.gameId);
      const gameMatch = game?.name.toLowerCase().includes(query);
      // Search in team names
      const teamNames = Object.keys(play.teams).join(' ').toLowerCase();
      const teamMatch = teamNames.includes(query);
      // Search in player names
      const playerNames = Object.values(play.teams)
        .flat()
        .map(playerId => players.find(p => p.id === playerId)?.name || '')
        .join(' ')
        .toLowerCase();
      const playerMatch = playerNames.includes(query);
      // Search in comment
      const commentMatch = play.comment?.toLowerCase().includes(query);
      // Search in date
      const dateMatch = play.playDate.toLowerCase().includes(query);

      if (!gameMatch && !teamMatch && !playerMatch && !commentMatch && !dateMatch) {
        return false;
      }
    }

    // Filter by game
    if (selectedGame && play.gameId !== selectedGame) {
      return false;
    }

    // Filter by player count
    if (selectedPlayerCount) {
      const playerCount = getPlayerCountForPlay(play);
      if (selectedPlayerCount !== playerCount.toString()) {
        return false;
      }
    }

    // Filter by players - play must include ALL selected players
    if (selectedPlayers.length > 0) {
      const playPlayerIds = Object.values(play.teams).flat();
      const hasAllSelectedPlayers = selectedPlayers.every(playerId =>
        playPlayerIds.includes(playerId)
      );
      if (!hasAllSelectedPlayers) {
        return false;
      }
    }

    // Filter by leagues - play must be associated with at least ONE selected league
    if (selectedLeagues.length > 0) {
      const hasAnySelectedLeague = selectedLeagues.some(leagueId =>
        play.leagueIds.includes(leagueId)
      );
      if (!hasAnySelectedLeague) {
        return false;
      }
    }

    return true;
  });

  const handleClearFilters = () => {
    setSelectedGame('');
    setSelectedPlayerCount('');
    setSelectedPlayers([]);
    setSelectedLeagues([]);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden">
      {/* Header */}
      <PageHeader className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-fluid-h1 font-bold text-heading">Play Sessions</h1>
          <p className="mt-2 text-body">
            View and manage recorded game sessions
          </p>
        </div>
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={handleRecalculateRatings}
              disabled={recalcPoller.isProcessing}
              className="inline-flex items-center px-4 py-2 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrophyIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{recalcPoller.isProcessing ? recalcPoller.statusText : 'Recalculate Ratings'}</span>
            </button>
          )}
          <Link
            data-track="play_new"
            to="/plays/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Record Play</span>
          </Link>
        </div>
      </PageHeader>

      {/* Statistics Summary */}
      {plays.length > 0 && (() => {
        const hasFilters = !!(selectedGame || selectedPlayerCount || selectedPlayers.length > 0 || selectedLeagues.length > 0);
        return (
          <StatCardsCarousel
            title={`Play Statistics${hasFilters ? ' (Filtered View)' : ''}`}
            stats={[
              {
                icon: PlayIcon,
                value: hasFilters ? filteredPlays.length : (playStats?.totalPlays ?? filteredPlays.length),
                label: hasFilters ? 'Filtered Play Sessions' : 'Total Play Sessions',
                color: 'blue',
              },
              {
                icon: PuzzlePieceIcon,
                value: hasFilters ? new Set(filteredPlays.map(play => play.gameId)).size : (playStats?.uniqueGames ?? new Set(filteredPlays.map(play => play.gameId)).size),
                label: hasFilters ? 'Filtered Unique Games' : 'Total Unique Games',
                color: 'purple',
              },
            ]}
          />
        );
      })()}

      {/* Filters */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-fluid-h2 font-semibold text-heading">Play Sessions</h2>
          <div className="flex items-center gap-2">
            <SearchButton
              isExpanded={searchExpanded}
              onToggle={() => setSearchExpanded(!searchExpanded)}
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search plays..."
            />
            <FilterButton
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              isExpanded={isFilterExpanded}
              hasActiveFilters={!!(selectedGame || selectedPlayerCount || selectedPlayers.length > 0 || selectedLeagues.length > 0)}
            />
          </div>
        </div>
        <p className="text-body text-sm mt-1">
          {searchQuery ? (
            <>Showing {filteredPlays.length} of {plays.length} play sessions</>
          ) : (
            <>Filter and view recorded game sessions</>
          )}
        </p>
      </div>
      <PlayFilterWidget
        games={games}
        players={players}
        plays={plays}
        leagues={leagues}
        selectedGame={selectedGame}
        selectedPlayerCount={selectedPlayerCount}
        selectedPlayers={selectedPlayers}
        selectedLeagues={selectedLeagues}
        onSelectedGameChange={setSelectedGame}
        onSelectedPlayerCountChange={setSelectedPlayerCount}
        onSelectedPlayersChange={setSelectedPlayers}
        onSelectedLeaguesChange={setSelectedLeagues}
        onClearFilters={handleClearFilters}
        filteredPlaysCount={filteredPlays.length}
        isExpanded={isFilterExpanded}
        onToggleExpanded={() => setIsFilterExpanded(!isFilterExpanded)}
        renderFilterButton={() => null}
      />

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {recalcPoller.error && (
        <div className="mb-4 text-sm text-red-600">{recalcPoller.error}</div>
      )}

      {/* Plays List */}
      <PlayList
        plays={filteredPlays}
        players={players}
        games={games}
        leagues={leagues}
        showActions={true}
        showPlayerDetails={true}
        showFullDetails={true}
        showPlayImages={true}
        onDeletePlay={handleDeletePlay}
        onReactionChange={refetchPlays}
      />
    </div>
  );
};

export default PlayListPage;